import React, { useState, useEffect, useRef } from 'react';
import { Form } from 'react-bootstrap';
import { FaCalendarAlt, FaChevronDown } from 'react-icons/fa';
import { FiClock } from "react-icons/fi";
import DatePicker from 'react-datepicker';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import DeleteIcon from "../../assets/images/fluent-color_warning-20.svg";
import * as apicall from '../../components/Apicall';
import { toast } from 'react-toastify';
import AppointmentModal from './AppointmentModal';
import PhoneInput from 'react-phone-input-2';
import ConfirmationModal from './ConfirmationModal';
import FollowUpConfirmModal from './FollowUpConfirmModal';
import 'react-phone-input-2/lib/style.css';

const NewAppointment = ({ onClose, initialData = null, mode = 'new', timeSlots, bookingId, callApi, holidayDates }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showFollowUpConfirm, setFollowUpConfirm] = useState(false);
  const [showNoShowConfirm, setShowNoShowConfirm] = useState(false);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const [calendarPickerOpen, setCalendarPickerOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(initialData?.details?.booking_date);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [insuranceProviders, setInsuranceProviders] = useState([]);
  const [showInsuranceProviderDropdown, setShowInsuranceProviderDropdown] = useState(false);
  const [insuranceProviderSearchTerm, setInsuranceProviderSearchTerm] = useState('');
  const insuranceProviderRef = useRef(null);
  const timeSlotRef = useRef(null);
  const userId = initialData?.details?.user_id;
  // const doctorId = initialData?.details?.doctor_id;
  const bookedDate = initialData?.details?.booking_date;
  const [currentStatus, setCurrentStatus] = useState(initialData?.details?.status || null);
  const [appointmentTimeState, setAppointmentTimeState] = useState(initialData?.appointmentTime || '');
  const [noShowMarked, setNoShowMarked] = useState(false);
  const [timeSlotsPassed, setTimeSlotsPassed] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [doctorPage, setDoctorPage] = useState(1);
  const [insuranceProvidersLoaded, setInsuranceProvidersLoaded] = useState(false);
  const vendorId = localStorage.getItem('vendorId');
  const [hasMoreDoctors, setHasMoreDoctors] = useState(true);
  const doctorDropdownRef = useRef(null);
  const [showDoctorDropdown, setShowDoctorDropdown] = useState(false);
  const [doctorSearchTerm, setDoctorSearchTerm] = useState('');
  const [doctorId, setDoctorId] = useState(initialData?.details?.doctor_id)
  const DOCTORS_PER_PAGE = 10;

  // Form validation schema
  const validationSchema = Yup.object().shape({
    patientName: Yup.string()
      .required('Patient name is required')
      .min(2, 'Name must be at least 2 characters')
      .max(50, 'Name must be at most 50 characters'),
    age: Yup.number()
      .required('Age is required')
      .positive('Age must be positive')
      .integer('Age must be an integer')
      .max(120, 'Age must be less than 120'),
    gender: Yup.string().required('Gender is required'),
    mobileNumber: Yup.string().required('Mobile number is required'),
    insuranceNumber: Yup.string().nullable(),
    insuranceProvider: Yup.string().nullable(),
    doctorName: Yup.string().required('Doctor name is required'),
    appointmentDate: Yup.date().required('Appointment date is required'),
    appointmentTime: Yup.string().required('Appointment time is required'),
  });


  const fetchDoctors = async (page = 1, isLoadingMore = false) => {
    try {
      if (page === 1) {
        setLoadingDoctors(true);
      }

      let url = `get_service_providers?vendor_id=${vendorId}&role=5&limit=${DOCTORS_PER_PAGE}&page=${page}`;

      if (doctorSearchTerm && doctorSearchTerm.trim() !== '') {
        url += `&name=${encodeURIComponent(doctorSearchTerm.trim())}`;
      }

      const response = await apicall.get(url);

      if (response.status === 200) {
        const formattedDoctors = response.data.map(doctor => ({
          id: doctor.id,
          serviceProvider_id: doctor?.service_provider,
          user_id: doctor.user_id,
          name: `${doctor.first_name} ${doctor.last_name || ''}`.trim(),
          specialization: doctor.specilisation
        }));

        if (page === 1) {
          setDoctors(formattedDoctors);
        } else {
          setDoctors(prev => [...prev, ...formattedDoctors]);
        }

        setHasMoreDoctors(formattedDoctors.length === DOCTORS_PER_PAGE);
        setDoctorPage(page);
      }
    } catch (error) {
      console.error('Error fetching doctors:', error);
      toast.error('Failed to fetch doctors');
    } finally {
      setLoadingDoctors(false);
    }
  };

  // Fetch insurance providers on component mount
  useEffect(() => {
    const fetchInsuranceProviders = async () => {
      try {
        const response = await apicall.get('insuranceProviders');
        if (response && response.data) {
          setInsuranceProviders(response.data || []);
          setInsuranceProvidersLoaded(true);
        }
      } catch (error) {
        toast.error('Failed to fetch insurance providers');
        setInsuranceProvidersLoaded(true);
      }
    };

    fetchInsuranceProviders();
    if (vendorId) {
      fetchDoctors();
    }

  }, []);

  // Handle click outside for dropdowns
  useEffect(() => {
    function handleClickOutside(event) {
      if (insuranceProviderRef.current && !insuranceProviderRef.current.contains(event.target)) {
        setShowInsuranceProviderDropdown(false);
      }
      if (timeSlotRef.current && !timeSlotRef.current.contains(event.target)) {
        setShowTimeDropdown(false);
      }
      if (doctorDropdownRef.current && !doctorDropdownRef.current.contains(event.target)) {
        setShowDoctorDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (initialData) {
      fetchDoctorAvailability(doctorId, selectedDate);
      setAppointmentTimeState(initialData.appointmentTime || '');
      setNoShowMarked(initialData?.details?.status === 12);
    }
  }, [selectedDate]);

  const isTodayAppointment = () => {
    if (!bookedDate) return false;
    const today = new Date().toISOString().split('T')[0];
    return bookedDate === today;
  };

  const checkAppointmentTimePassed = () => {
    if (!bookedDate || !appointmentTimeState) {
      setTimeSlotsPassed(false);
      return false;
    }

    const today = new Date();
    const appointmentDate = new Date(bookedDate);

    if (today.toISOString().split('T')[0] !== bookedDate) {
      setTimeSlotsPassed(false);
      return false;
    }

    const timeSlots = appointmentTimeState.includes(',')
      ? appointmentTimeState.split(',')
      : [appointmentTimeState];

    const allPassed = timeSlots.every(slot => {
      const trimmedSlot = slot.trim();
      const [time, modifier] = trimmedSlot.split(' ');
      if (!time || !modifier) return false;

      let [hours, minutes] = time.split(':');
      hours = parseInt(hours, 10);
      minutes = parseInt(minutes, 10);

      if (modifier.toUpperCase() === 'PM' && hours < 12) hours += 12;
      if (modifier.toUpperCase() === 'AM' && hours === 12) hours = 0;

      const slotTime = new Date(appointmentDate);
      slotTime.setHours(hours, minutes + 15, 0, 0);

      return today > slotTime;
    });

    setTimeSlotsPassed(allPassed);
    return allPassed;
  };

  useEffect(() => {
    if (isTodayAppointment() && !noShowMarked && currentStatus !== 15) {
      checkAppointmentTimePassed();
      const interval = setInterval(checkAppointmentTimePassed, 60000);
      return () => clearInterval(interval);
    }
  }, [isTodayAppointment(), appointmentTimeState, noShowMarked, currentStatus]);

  const isPastAppointment = () => {
    if (!bookedDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const appointmentDate = new Date(bookedDate);
    return appointmentDate < today;
  };

  const isFutureAppointment = () => {
    if (!bookedDate) return false;
    const today = new Date();
    const appointmentDate = new Date(bookedDate);
    return appointmentDate > today;
  };

  const canCheckIn = isTodayAppointment() && currentStatus !== 11 && currentStatus !== 15 && !noShowMarked;
  const canCheckOut = isTodayAppointment() && currentStatus === 11 && !noShowMarked;
  // const canNoShow = ((isTodayAppointment() && timeSlotsPassed) || isPastAppointment()) && !noShowMarked;
  // const noShowDisabled = !canNoShow || isFutureAppointment() || currentStatus === 15 || noShowMarked;
  const canNoShow = ((isTodayAppointment() && timeSlotsPassed) || isPastAppointment()) && !noShowMarked && currentStatus !== 11;
  // Update the noShowDisabled to include currentStatus === 11
  const noShowDisabled = !canNoShow || isFutureAppointment() || currentStatus === 15 || noShowMarked || currentStatus === 11;

  //api function to fetch available slots
  const fetchDoctorAvailability = async (doctorId, selectedDate) => {
    if (!doctorId || !selectedDate) return;

    try {
      const response = await apicall.get(`get_available_slots/${doctorId}/${selectedDate}`);
      if (response && response.data) {
        setAvailableTimeSlots(response.data.available_slots || []);
      } else {
        setAvailableTimeSlots([]);
      }
    } catch (error) {
      toast.error('Failed to fetch available slots');
      setAvailableTimeSlots([]);
    }
  };


  useEffect(() => {
    if (insuranceProvidersLoaded && initialData?.details?.insurance_provider) {
      formik.setFieldValue('insuranceProvider', initialData.details.insurance_provider);
    }
  }, [insuranceProvidersLoaded, initialData]);

  const formik = useFormik({
    initialValues: {
      patientName: initialData?.patientName || '',
      age: initialData?.age || '',
      gender: initialData?.gender || 'Male',
      mobileNumber: initialData?.details?.patient_phone || '',
      insuranceNumber: initialData?.insuranceNumber || '',
      insuranceProvider: initialData?.details?.insurance_provider || '',
      doctorName: initialData?.doctorName || '',
      doctorId: initialData?.details?.doctor_id || '',
      service_provider: initialData?.service_provider || 0,
      appointmentDate: initialData?.appointmentDate || '',
      appointmentTime: initialData?.appointmentTime || '',
      selectedTimeSlots: initialData?.selectedTimeSlots || []
    },
    validationSchema,
    onSubmit: (values) => {
      if (mode === 'edit') {
        handleRescheduleAppointment("reschedule");
      } else {
        console.log('New appointment data:', values);
      }
    },
  });



  useEffect(() => {
    if (formik.values.appointmentTime) {
      setAppointmentTimeState(formik.values.appointmentTime);
      checkAppointmentTimePassed();
    }
  }, [formik.values.appointmentTime]);

  const handleYesClick = () => {
    setFollowUpConfirm(false)
    setShowModal(true);

  };

  const handleModalClose = () => {
    setShowModal(false);
    onClose();
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    const formattedSlot = convertTo24HourFormat([formik.values.appointmentTime]);
    const payload = {
      booking_id: bookingId,
      appointment_date: formik.values.appointmentDate,
      time_slots: [formattedSlot],
      status: 14
    };

    try {
      const response = await apicall.post(payload, 'update-booking');
      if (response.status === 200) {
        toast.success('Appointment Deleted successfully', {
          autoClose: 2000,
          onClose: () => {
            window.location.reload();
          },
        });
        setShowDeleteConfirm(false);
        onClose();
      }
    } catch (error) {
      toast.error(error?.message || 'Failed to delete appointment');
    }
  };

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';

  const handleTimeSlotClick = (slot) => {
    const currentSlots = [...formik.values.selectedTimeSlots];
    const startTime = slot.split(" - ")[0].trim();
    const slotIndex = currentSlots.indexOf(startTime);

    if (slotIndex === -1) {
      currentSlots.push(startTime);
    } else {
      currentSlots.splice(slotIndex, 1);
    }

    const formattedTimes = currentSlots
      .sort((a, b) => a.localeCompare(b))
      .map(time => {
        const [hourStr, minute] = time.split(":");
        const hour = parseInt(hourStr, 10);
        return `${hour < 10 ? "0" + hour : hour}:${minute}`;
      })
      .join(", ");

    formik.setFieldValue('selectedTimeSlots', currentSlots);
    formik.setFieldValue('appointmentTime', formattedTimes);
    setAppointmentTimeState(formattedTimes);
  };

  const convertTo24HourFormat = (slots) => {
    return slots.map(slot => {
      const strSlot = String(slot).trim();
      const [time, modifier] = strSlot.split(' ');
      if (!time || !modifier) return null;

      let [hours, minutes] = time.split(':');
      hours = parseInt(hours, 10);
      if (modifier.toUpperCase() === 'PM' && hours < 12) hours += 12;
      if (modifier.toUpperCase() === 'AM' && hours === 12) hours = 0;

      return `${hours.toString().padStart(2, '0')}:${minutes}`;
    }).filter(Boolean);
  };

  const handleRescheduleAppointment = async (statusUpdate) => {
    const formattedSlot = convertTo24HourFormat(formik.values.selectedTimeSlots);

    let statusPayload;
    switch (statusUpdate) {
      case 'No show':
        statusPayload = { status: 12 };
        setNoShowMarked(true);
        break;
      case 'check in':
        statusPayload = { status: 11 };
        break;
      case 'check out':
        statusPayload = { status: 15 };
        break;
      default:
        statusPayload = {};
    }

    const payload = {
      booking_id: bookingId,
      appointment_date: formik.values.appointmentDate,
      time_slots:
        formik.values.selectedTimeSlots.length === 0
          ? convertTo24HourFormat([formik.values.appointmentTime])
          : formattedSlot,
      user_id: userId,
      insurance_number: formik.values.insuranceNumber,
      insurance_provider: formik.values.insuranceProvider,
      ...statusPayload,
    };

    try {
      const response = await apicall.post(payload, 'update-booking');
      if (response.status === 200) {
        const messages = {
          'No show': 'Appointment marked as No Show successfully',
          'check in': 'Appointment checked in successfully',
          'check out': 'Appointment checked out successfully',
          'reschedule': 'Appointment Rescheduled successfully',
        };

        toast.success(messages[statusUpdate], {
          autoClose: 2000,
          onClose: () => {
            if (statusUpdate !== 'check out') {
              window.location.reload();
            }
          }
        });

        setCurrentStatus(statusPayload.status || currentStatus);

        if (statusUpdate !== 'check out') {
          onClose();
        } else {
          setFollowUpConfirm(true);
        }
      }

    } catch (error) {
      toast.error('Failed to update appointment');
      if (statusUpdate === 'No show') {
        setNoShowMarked(false);
      }
    }
  };


  const handleCheckIn = () => handleRescheduleAppointment("check in");
  const handleNoShow = () => {
    setShowNoShowConfirm(false);
    handleRescheduleAppointment("No show");
  };
  const handleCheckout = () => handleRescheduleAppointment("check out");
  const handleReScheduleRedirect = () => {
    onClose('edit');
    setShowNoShowConfirm(false);
  };



  return (
    <div className="appointment-form">
      <Form onSubmit={formik.handleSubmit}>
        {/* Patient Information Section */}
        <div className="mb-3">
          <Form.Label>Patient Name</Form.Label>
          <Form.Control
            type="text"
            name="patientName"
            value={formik.values.patientName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={true}
            className='label-bgcolor'
            isInvalid={formik.touched.patientName && !!formik.errors.patientName}
          />
          <Form.Control.Feedback type="invalid">
            {formik.errors.patientName}
          </Form.Control.Feedback>
        </div>

        <div className="mb-3">
          <Form.Label>Age</Form.Label>
          <Form.Control
            type="text"
            name="age"
            value={formik.values.age}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={true}
            className='label-bgcolor'
            isInvalid={formik.touched.age && !!formik.errors.age}
          />
          <Form.Control.Feedback type="invalid">
            {formik.errors.age}
          </Form.Control.Feedback>
        </div>

        <div className="mb-3">
          <Form.Label>Gender</Form.Label>
          <Form.Select
            name="gender"
            value={formik.values.gender}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={true}
            className="form-input label-bgcolor "

            isInvalid={formik.touched.gender && !!formik.errors.gender}
          >
            <option value="Male">Male</option>
            <option value="Female">Female</option>
          </Form.Select>
          <Form.Control.Feedback type="invalid">
            {formik.errors.gender}
          </Form.Control.Feedback>
        </div>

        <div className="mb-3">
          <Form.Label>Mobile Number</Form.Label>
          <PhoneInput
            country={'ae'}
            onlyCountries={['ae', 'in']}
            value={formik.values.mobileNumber}
            onChange={(value) => {
              const cleanedValue = value.replace(/\D/g, '');
              formik.setFieldValue('mobileNumber', cleanedValue);
            }}
            inputStyle={{
              width: '100%',
              height: '40px',
              backgroundColor: '#F8F9FA',
              borderColor: formik.touched.mobileNumber && formik.errors.mobileNumber ? '#dc3545' : '#ced4da'
            }}
            inputProps={{
              name: 'mobileNumber',
              required: true,
              autoFocus: false
            }}
            disabled={true}
          />
          <Form.Control.Feedback type="invalid">
            {formik.errors.mobileNumber}
          </Form.Control.Feedback>
        </div>

        <div className="mb-3">
          <Form.Label>
            Insurance Number <span className="text-muted">(Optional)</span>
          </Form.Label>
          <Form.Control
            type="text"
            name="insuranceNumber"
            value={formik.values.insuranceNumber}
            onChange={formik.handleChange}
            disabled={isViewMode}
            style={{ backgroundColor: '#fff' }}
          />
        </div>

        {/* Insurance Provider Dropdown */}
        <div className="mb-3" style={{ position: 'relative' }} ref={insuranceProviderRef}>
          <label className="form-labels">
            Insurance Provider <span className="text-muted">(Optional)</span>
          </label>

          <div
            onClick={() => !isViewMode && setShowInsuranceProviderDropdown(!showInsuranceProviderDropdown)}
            style={{ border: '1px solid #E9ECEF' }}
            className={`label-dropdown-toggle ${isEditMode ? 'bg-white' : 'label-bgcolor'}`}
          >
            <span className="">
              {insuranceProviders.find(p => p.insurance_provider_id === formik.values.insuranceProvider)?.name || 'Select Insurance Provider'}
            </span>
            <span className="text-dark"> <FaChevronDown /></span>
          </div>

          {showInsuranceProviderDropdown && (
            <div className="label-provider-dropdown">
              <div className="p-2" style={{ borderBottom: '1px solid #E9ECEF' }}>
                <Form.Control
                  type="text"
                  placeholder="Search"
                  value={insuranceProviderSearchTerm}
                  onChange={(e) => setInsuranceProviderSearchTerm(e.target.value)}
                  className="form-input label-search-input"
                />
              </div>

              {insuranceProviders.length > 0 ? (
                insuranceProviders
                  .filter(p => (p.name || '').toLowerCase().includes(insuranceProviderSearchTerm.toLowerCase()))
                  .map(p => (
                    <div
                      key={p.id}
                      className="p-2 list-item"
                      style={{
                        background: formik.values.insuranceProvider === p.insurance_provider_id ? '#E3F2FF' : 'white',
                      }}
                      onClick={() => {
                        formik.setFieldValue('insuranceProvider', p.insurance_provider_id);
                        setShowInsuranceProviderDropdown(false);
                        setInsuranceProviderSearchTerm('');
                      }}
                    >
                      {p.name}
                    </div>
                  ))
              ) : (
                <div className="p-2 text-muted">No insurance providers found</div>
              )}
            </div>
          )}
        </div>


        {/* Doctor Information Section */}
        <div className="mb-3">
          <div className="mb-3">
            <Form.Label>Doctor Name</Form.Label>
            <Form.Control
              type="text"
              name="doctorName"
              value={`Dr. ${formik.values.doctorName}`}
              onChange={formik.handleChange}
              disabled={true}
              className='label-bgcolor'
            />
          </div>
          <Form.Control.Feedback type="invalid">
            {formik.errors.doctorName}
          </Form.Control.Feedback>
        </div>

        {/* Appointment Time Section */}
        <div className="mb-4">
          <Form.Label>Date & Time</Form.Label>
          <div className="d-flex gap-2">
            <div className="position-relative flex-grow-1">
              <Form.Control
                type="text"
                name="appointmentDate"
                value={formik.values.appointmentDate}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={isViewMode}
                className={isEditMode ? 'bg-white' : 'label-bgcolor'}
                isInvalid={formik.touched.appointmentDate && !!formik.errors.appointmentDate}
              />
              <span className="position-absolute top-50 end-0 translate-middle-y me-2">
                <FaCalendarAlt
                  className="text-muted"
                  onClick={() => !isViewMode && setCalendarPickerOpen(!calendarPickerOpen)}
                />
              </span>
              {calendarPickerOpen && (
                <div style={{ position: 'absolute', top: '45px', left: 0, zIndex: 9999 }}>
                  <DatePicker
                    selected={
                      formik.values.appointmentDate
                        ? (() => {
                          const [year, month, day] = formik.values.appointmentDate.split('-');
                          return new Date(year, month - 1, day); // Safe local date
                        })()
                        : null
                    }
                    onChange={(date) => {
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      const formattedDate = `${year}-${month}-${day}`; // Local-safe formatting

                      formik.setFieldValue('appointmentDate', formattedDate);
                      setSelectedDate(formattedDate);
                      setCalendarPickerOpen(false);
                    }}
                    excludeDates={holidayDates?.map((dateStr) => {
                      const [year, month, day] = dateStr.split("-");
                      return new Date(year, month - 1, day);
                    }) || []}
                    filterDate={(date) => {
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, "0");
                      const day = String(date.getDate()).padStart(2, "0");
                      const dateString = `${year}-${month}-${day}`;
                      return !holidayDates?.includes(dateString);
                    }}
                    dayClassName={(date) => {
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, "0");
                      const day = String(date.getDate()).padStart(2, "0");
                      const dateString = `${year}-${month}-${day}`;
                      return holidayDates?.includes(dateString) ? "disabled-day" : null;
                    }}
                    minDate={new Date()}
                    inline
                  />
                </div>
              )}
              <Form.Control.Feedback type="invalid">
                {formik.errors.appointmentDate}
              </Form.Control.Feedback>
            </div>
            <div className="position-relative flex-grow-1" ref={timeSlotRef}>
              <Form.Control
                type="text"
                name="appointmentTime"
                value={
                  formik.values.selectedTimeSlots.length === 0
                    ? formik.values.appointmentTime
                    : formik.values.selectedTimeSlots.length <= 2
                      ? formik.values.selectedTimeSlots.join(', ')
                      : `${formik.values.selectedTimeSlots.slice(0, 2).join(', ')}...`
                }
                readOnly
                disabled={isViewMode}
                className={isEditMode ? 'bg-white' : 'label-bgcolor'}
                onClick={() => !isViewMode && setShowTimeDropdown(!showTimeDropdown)}
                isInvalid={formik.touched.appointmentTime && !!formik.errors.appointmentTime}
              />
              <span
                onClick={() => !isViewMode && setShowTimeDropdown(!showTimeDropdown)}
                className="position-absolute top-50 end-0 translate-middle-y me-2"
                style={{ cursor: isViewMode ? 'not-allowed' : 'pointer' }}
              >
                <FiClock className="text-muted" />
              </span>
              <Form.Control.Feedback type="invalid">
                {formik.errors.appointmentTime}
              </Form.Control.Feedback>

              {showTimeDropdown && (
                <div style={{ position: 'relative' }}>
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    padding: '8px',
                    marginTop: '4px',
                    border: '1px solid #E3EBF8',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
                    gap: '8px',
                  }}>
                    {availableTimeSlots.map((slot, index) => {
                      const startTime = slot.split(" - ")[0].trim();
                      const isSelected = formik.values.selectedTimeSlots.includes(startTime);
                      return (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handleTimeSlotClick(slot)}
                          style={{
                            backgroundColor: isSelected ? '#E3F2FF' : '#F0F7FF',
                            borderRadius: '20px',
                            padding: '6px 12px',
                            fontSize: '13px',
                            cursor: 'pointer',
                            textAlign: 'center',
                            border: isSelected ? '1px solid #0D6EFD' : '1px solid #E3EBF8',
                            color: isSelected ? '#0D6EFD' : '#212529',
                            transition: 'all 0.2s ease',
                            width: '100%',
                            '&:hover': {
                              backgroundColor: '#E3F2FF',
                              borderColor: '#0D6EFD'
                            }
                          }}
                        >
                          {startTime}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        {isViewMode && (
          <>
            <div className="d-flex gap-2 mt-4">
              <button
                type="button"
                className="btn flex-grow-1"
                style={{
                  backgroundColor: '#fff',
                  color: '#C9194A',
                  borderRadius: '16px',
                  border: '1px solid #DEE2E6'
                }}
                onClick={handleDelete}
              >
                Delete Appointment
              </button>
              <button
                type="button"
                className="btn flex-grow-1"
                style={{
                  color: '#4B52EC',
                  border: 'none',
                  borderRadius: '16px',
                  border: '1px solid #DEE2E6'
                }}
                onClick={() => onClose('edit')}
              >
                Re-Schedule
              </button>
              <button
                type="button"
                className="btn flex-grow-1"
                style={{
                  backgroundColor: noShowDisabled ? '#fff' : '#FFF0F0',
                  color: noShowDisabled ? '#6C757D' : '#C9194A',
                  borderRadius: '16px',
                  border: noShowDisabled ? '1px solid #DEE2E6' : '1px solid #C9194A',
                  cursor: noShowDisabled ? 'not-allowed' : 'pointer',
                  opacity: noShowDisabled ? 0.6 : 1
                }}
                onClick={() => !noShowDisabled && setShowNoShowConfirm(true)}
                disabled={noShowDisabled}
                title={
                  noShowMarked ? "No Show already marked" :
                    currentStatus === 11 ? "No Show not available after check-in" :
                      isFutureAppointment() ? "No Show not available for future appointments" :
                        currentStatus === 15 ? "No Show not available for completed appointments" :
                          isTodayAppointment() && !timeSlotsPassed ? "No Show available after appointment time has passed" :
                            "Mark as No Show"
                }
              >
                No Show
              </button>
            </div>

            <div className="d-flex gap-2 mt-3">
              <button
                type="button"
                className="btn flex-grow-1"
                style={{
                  backgroundColor: canCheckIn ? '#C9194A' : '#6C757D',
                  color: '#fff',
                  borderRadius: '16px',
                  border: 'none',
                  cursor: canCheckIn ? 'pointer' : 'not-allowed'
                }}
                onClick={canCheckIn ? handleCheckIn : null}
                disabled={!canCheckIn}
                title={noShowMarked ? "Check-In disabled after No Show" : ""}
              >
                Complete Check-In
              </button>
              <button
                type="button"
                className="btn flex-grow-1"
                style={{
                  backgroundColor: canCheckOut ? '#C9194A' : '#6C757D',
                  borderRadius: '16px',
                  color: '#fff',
                  border: 'none',
                  cursor: canCheckOut ? 'pointer' : 'not-allowed'
                }}
                onClick={canCheckOut ? handleCheckout : null}
                disabled={!canCheckOut}
                title={noShowMarked ? "Check-Out disabled after No Show" : ""}
              >
                Complete Check-Out
              </button>
            </div>
          </>
        )}

        {(mode === 'new' || isEditMode) && (
          <div className="d-flex gap-2 mt-4">
            <button
              type="submit"
              className="btn flex-grow-1"
              style={{
                backgroundColor: isEditMode ? '#C9194A' : '#007AFF',
                color: '#fff',
                border: 'none',
                borderRadius: '16px'
              }}
            >
              {isEditMode ? 'Update' : 'Save'}
            </button>
            <button
              type="button"
              className="btn flex-grow-1"
              style={{
                backgroundColor: '#fff',
                color: '#6C757D',
                border: '1px solid #DEE2E6',
                borderRadius: '16px'
              }}
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        )}
      </Form>

      {/* Confirmation Modals */}
      {showDeleteConfirm && (
        <ConfirmationModal
          show={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          icon={DeleteIcon}
          title="Delete Appointment"
          message="Are you sure want to delete this Appointment?"
          primaryText="Delete"
          secondaryText="Cancel"
          onPrimary={() => {
            confirmDelete();
            setShowDeleteConfirm(false);
          }}
        />
      )}

      <AppointmentModal
        holidayDates={holidayDates}
        show={showModal}
        onHide={handleModalClose}
        reqData={formik.values}
        info={initialData?.details}
        timeSlots={timeSlots}
      />

      {showFollowUpConfirm && (
        <FollowUpConfirmModal
          show={showFollowUpConfirm}
          onYes={handleYesClick}
          onNo={() => setFollowUpConfirm(false)}
        />

      )}

      {showNoShowConfirm && (
        <ConfirmationModal
          show={showNoShowConfirm}
          onClose={() => setShowNoShowConfirm(false)}
          icon={DeleteIcon}
          title="Patient didn't visit"
          message="Do you want to close the appointment?"
          primaryText="Yes, close"
          secondaryText="Reschedule"
          onPrimary={() => {
            handleNoShow()
          }}
          onSecondary={() => {
            handleReScheduleRedirect()
          }}
        />
      )}
    </div>
  );
};

export default NewAppointment;